/* tslint:disable: max-line-length no-console */

import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Inject, Injectable, Optional } from '@angular/core';
import { CustomError } from './custom-error.service';
import {
    IBreadcrumb,
    IContextDetails,
    IErrorHandlerOptions,
    IErrorOptions,
    IHint,
    ILoggerService,
    INotificationService,
    OfLoggerService,
    OfNotificationService
} from './error-handler-interfaces.service';
import {
    AuthErrorStrategy,
    ConnectionErrorStrategy,
    CustomErrorStrategy,
    GenericErrorStrategy,
    HttpDebugStrategy,
    HttpErrorStrategy,
    HttpRefreshErrorStrategy,
    ShowNotificationStrategy,
    TemplateErrorStrategy
} from './error-strategies.service';

/** An adapter function for logging errors.
 * @arg error An error object.
 * @arg errorDetails Additional details about the error such as `level`, `tags`, etc.
 */
@Injectable({ providedIn: 'root' })
export class ErrorHandlerService implements ErrorHandler {
    errorId: string;
    private notificationStyle =
        'padding: 1rem; background: #bbdefb; border: 2px solid #8aacc8; font-size: .9rem; line-height: 3rem';
    private contextStyle =
        'padding: 1rem; background: #ffffa6; border: 2px solid #dce775; font-size: .9rem; line-height: 3rem';
    private logStyle =
        'padding: 1rem; background: #efdcd5; border: 2px solid #bcaaa4; font-size: .9rem; line-height: 3rem';
    private understateStyle = 'color: #666; background: #fff; font-size: .7rem;';

    // Built in logger and notifications.

    /** Access the logger service to call it's methods directly. */
    public logger: ILoggerService = this._logger || {
        logError: (err, details = {}) => {
            console.log(
                '%c📜 LOG ERROR:',
                this.logStyle,
                err,
                '\nDETAILS:\n ' + JSON.stringify(details)
            );
        },
        logMessage: (message) => {
            console.log(`%c📜 LOG MESSAGE: ${message}`, this.logStyle, '\n', message);
        },
        logBreadcrumb: (details: IBreadcrumb, hint: IHint) => {
            console.log(
                '%c📜 LOG BREADCRUMB:',
                this.understateStyle,
                '\n',
                details,
                hint
            );
        },
        logEvent: (event) => {
            console.log('%c📜 LOG EVENT:', this.logStyle, '\n', event);
        },
        configureContext: (context: IContextDetails) => {
            console.log('%c🗳️ UPDATE CONTEXT', this.contextStyle, '\n', context);
        }
    };

    /** Access the notification service to call it's methods directly. */
    public notifications: INotificationService = this._notifications || {
        show: (message = '') => {
            console.info(
                `%c🔔 ERROR NOTIFICATION: ${message}`,
                'padding: 1rem; background: #bbdefb; border: 2px solid #8aacc8; font-size: .9rem;'
            );
        },
        showError: (message = '') => {
            console.info(
                `️%c🔔 ERROR NOTIFICATION: Sorry, there\'s been an error! ${message}`,
                this.notificationStyle
            );
        },
        notify: (message = '', priority = 0, options = { viewOptions: {} }) => {
            let nextSteps = [];
            if (options.viewOptions && options.viewOptions.controls) {
                nextSteps = options.viewOptions.controls.map(
                    (c) => `> ${c.text || 'OK'}`
                );
            }
            console.info(
                `️%c⚠️ ERROR NOTIFICATION: Sorry, there\'s been an error! ${message}`,
                this.notificationStyle
            );
        }
    };

    constructor (
        @Inject('SHOW_ERROR_DETAILS')
        @Optional()
        private _showErrorDetails: boolean,
        @Inject('LOGGER_SERVICE') @Optional() private _logger: OfLoggerService,
        @Inject('NOTIFICATION_SERVICE')
        @Optional()
        private _notifications: OfNotificationService,
        @Inject('LOGIN_URL') @Optional() private _authRedirectUrl: string,
        @Inject('ERROR_HANDLER_OPTIONS')
        @Optional()
        private _userOptions: IErrorHandlerOptions
    ) {}

    private _options: IErrorHandlerOptions = {
        authRedirectUrl:
      this._authRedirectUrl || 'https://login.orthofi.com/sign-in',
        consoleLogErrors: this._showErrorDetails || false,
        ...this._userOptions
    };

    // options.authRedirectUrl will always exist.
    private getLoginUrlWithRedirect = (
        base = this._options.authRedirectUrl,
        redirect = window.location.href
    ) => `${base}/?redirect_url=${encodeURIComponent(redirect)}`;

    /**
   * Take an error and choose the correct strategy to handle it with.
   *
   * @arg error The error that to be handled.
   * @arg message A message to show in place of the default for the strategy.
   * @arg providedSupportId Override the support ID with a preset value.
   **/
    handleError = (
        error: CustomError | HttpErrorResponse | Error | any,
        message?: string,
        providedSupportId?: string
    ): void => {
        if (this._options.consoleLogErrors) {
            console.error('Handled Error:', error);
        }

        // Fallback on this strategy if nothing else gets assigned...
        let errorStrategy = new GenericErrorStrategy(
            this.notifications.notify,
            this.logger.logError,
            this.getLoginUrlWithRedirect(),
            message,
            providedSupportId
        );
        // Server or connection error happened
        if (error instanceof HttpErrorResponse) {
            if (!navigator.onLine) {
                errorStrategy = new ConnectionErrorStrategy(
                    this.notifications.notify,
                    this.logger.logError,
                    this.getLoginUrlWithRedirect(),
                    undefined,
                    providedSupportId
                );
            } else {
                switch (error.status) {
                    case 0:
                        errorStrategy = new HttpErrorStrategy(
                            this.notifications.notify,
                            this.logger.logError,
                            this.getLoginUrlWithRedirect(),
                            message ||
                'An unknown error was returned when attempting to connect. Details have been sent to our technical team, as it is likely due to our connection. However, this could also be caused by your firewall or browser addons. If the issue persists, please contact support.',
                            providedSupportId
                        );
                        break;

                    case 200:
                        errorStrategy = new HttpErrorStrategy(
                            this.notifications.notify,
                            this.logger.logError,
                            this.getLoginUrlWithRedirect(),
                            message ||
                'The application was unable to understand the response from the server. The details have been sent to our technical team. If the issue persists, please contact support.',
                            providedSupportId
                        );
                        break;

                    case 400:
                        errorStrategy = new HttpErrorStrategy(
                            this.notifications.notify,
                            this.logger.logError,
                            this.getLoginUrlWithRedirect(),
                            message ||
                'There was an issue communicating with the server. The details have been sent to our technical team. If the issue persists, please contact support.',
                            providedSupportId
                        );
                        break;

                    case 401:
                        errorStrategy = new AuthErrorStrategy(
                            this.notifications.notify,
                            this.logger.logError,
                            this.getLoginUrlWithRedirect()
                        );
                        break;

                    case 403:
                        // Example of overriding message
                        // errorStrategy = new AuthErrorStrategy(this._errordisplayFn, this._errorlogFn, this.getLoginUrlWithRedirect(), 'Oh no 403!');
                        errorStrategy = new HttpDebugStrategy(
                            this.notifications.notify,
                            this.logger.logError,
                            this.getLoginUrlWithRedirect(),
                            message ||
                'Your account is forbidden from accessing this information or functionality. If your account should have these permissions, please contact support.',
                            providedSupportId
                        );
                        break;

                    case 404:
                        errorStrategy = new HttpErrorStrategy(
                            this.notifications.notify,
                            this.logger.logError,
                            this.getLoginUrlWithRedirect(),
                            message ||
                'The information or page was not found. Please double check any information entered and if the issue persists, contact support.',
                            providedSupportId
                        );
                        break;

                    case 405:
                        errorStrategy = new HttpErrorStrategy(
                            this.notifications.notify,
                            this.logger.logError,
                            this.getLoginUrlWithRedirect(),
                            message ||
                'Something is preventing your browser from interacting with this application. This is likely an issue with our application or server. Please try again and if the issue persists, contact support.',
                            providedSupportId
                        );
                        break;

                    case 406:
                        errorStrategy = new HttpErrorStrategy(
                            this.notifications.notify,
                            this.logger.logError,
                            this.getLoginUrlWithRedirect(),
                            message ||
                'The application and your browser are able to communicate with each other, but are unwilling to exchange information. Please try again and if the issue persists, contact support.',
                            providedSupportId
                        );
                        break;

                    case 408:
                        errorStrategy = new HttpErrorStrategy(
                            this.notifications.notify,
                            this.logger.logError,
                            this.getLoginUrlWithRedirect(),
                            message ||
                'The application timed out when sending information to the server. If the issue persists, please try again later or contact support.',
                            providedSupportId
                        );
                        break;

                    case 409:
                        errorStrategy = new HttpRefreshErrorStrategy(
                            this.notifications.notify,
                            this.logger.logError,
                            this.getLoginUrlWithRedirect(),
                            message ||
                'The server is unable to save information coming from your browser due to a conflict. The information being sent may be badly formatted or out of date. If you\'re submitting a form, please double check any information entered and try again or refresh the page. If the issue persists, contact support.',
                            providedSupportId
                        );
                        break;

                    case 410:
                        errorStrategy = new HttpErrorStrategy(
                            this.notifications.notify,
                            this.logger.logError,
                            this.getLoginUrlWithRedirect(),
                            message ||
                'The information or page no longer appears to be available. Please double check any information entered and if the issue persists, contact support.',
                            providedSupportId
                        );
                        break;

                    case 413:
                        errorStrategy = new HttpErrorStrategy(
                            this.notifications.notify,
                            this.logger.logError,
                            this.getLoginUrlWithRedirect(),
                            message ||
                'The information being sent from your browser may be larger than we can operate on. If you\'re uploading a file, it\'s likely too large. Otherwise, clear your cookies and cache then try again. If the issue persists, contact support.',
                            providedSupportId
                        );
                        break;

                    case 414:
                        errorStrategy = new HttpErrorStrategy(
                            this.notifications.notify,
                            this.logger.logError,
                            this.getLoginUrlWithRedirect(),
                            message ||
                'Your browser is attempting to access a web address that is too long. This may or may not be the address in your address bar. Please try again and if the issue persists, contact support.',
                            providedSupportId
                        );
                        break;

                    case 429:
                        errorStrategy = new HttpErrorStrategy(
                            this.notifications.notify,
                            this.logger.logError,
                            this.getLoginUrlWithRedirect(),
                            message ||
                'Your browser has asked for information from the server too many times in too short a timespan. Please wait and try again later.',
                            providedSupportId
                        );
                        break;

                    case 440:
                        errorStrategy = new HttpErrorStrategy(
                            this.notifications.notify,
                            this.logger.logError,
                            this.getLoginUrlWithRedirect(),
                            message ||
                'The login service is currently unavailable. Please try again later.',
                            providedSupportId
                        );
                        break;

                    case 500:
                        errorStrategy = new HttpErrorStrategy(
                            this.notifications.notify,
                            this.logger.logError,
                            this.getLoginUrlWithRedirect(),
                            message ||
                'There was an issue on the server. The details have been sent to our technical team. If the issue persists, please contact support.',
                            providedSupportId
                        );
                        break;

                    case 502:
                        errorStrategy = new HttpErrorStrategy(
                            this.notifications.notify,
                            this.logger.logError,
                            this.getLoginUrlWithRedirect(),
                            message ||
                'There was an issue connecting to the server. The details have been sent to our technical team. If the issue persists, please contact support.',
                            providedSupportId
                        );
                        break;

                    case 503:
                        errorStrategy = new HttpErrorStrategy(
                            this.notifications.notify,
                            this.logger.logError,
                            this.getLoginUrlWithRedirect(),
                            message ||
                'A required service is currently unavailable. Please try again later.',
                            providedSupportId
                        );
                        break;

                    case 504:
                        errorStrategy = new HttpErrorStrategy(
                            this.notifications.notify,
                            this.logger.logError,
                            this.getLoginUrlWithRedirect(),
                            message ||
                'A required service did not respond. The details have been sent to our technical team. Please try again later.',
                            providedSupportId
                        );
                        break;

                    default:
                        errorStrategy = new HttpErrorStrategy(
                            this.notifications.notify,
                            this.logger.logError,
                            this.getLoginUrlWithRedirect(),
                            providedSupportId
                        );
                        break;
                }
            }
        }

        if (error.type && error.type === 'CustomError') {
            errorStrategy = new CustomErrorStrategy(
                this.notifications.notify,
                this.logger.logError,
                this.getLoginUrlWithRedirect(),
                message,
                providedSupportId
            );
        }

        // Handle ExpressionChangedAfterItHasBeenChecked template errors.
        if (
            error.message &&
      error.message.indexOf('Expression has changed after it was checked.') > 0
        ) {
            const templateFunctions: IErrorOptions =
        this.getErrorFunctions('expressionChanged');
            errorStrategy = new TemplateErrorStrategy(
                templateFunctions.displayFn,
                templateFunctions.loggerFn,
                this.getLoginUrlWithRedirect(),
                message,
                providedSupportId
            );
        }

        errorStrategy.execute(error);
    };

    /**
   * Retreive notification and logger functions for an `errorOptions` key from the error handler options.
   * - If options were provided for this type of error and the respective function is enabled, return either the function provided
   * in the options or the handler-level function.
   * - If options were provided for this type of error and the respective function is disabled, return a no-op.
   * - If no options were provided for this type, return the handler-level functions.
   */
    getErrorFunctions = (
        errorType: string,
        config: IErrorHandlerOptions = this._options
    ): IErrorOptions => {
        if (config && config.errorOptions && config.errorOptions[errorType]) {
            const options: IErrorOptions = config.errorOptions[errorType];
            return {
                displayFn:
          options.display !== false
              ? options.displayFn || this.notifications.notify
              : (message) => {},
                loggerFn:
          options.log !== false
              ? options.loggerFn || this.logger.logError
              : (error, errorDetails?) => {}
            };
        }
        return {
            displayFn: this.notifications.notify,
            loggerFn: this.logger.logError
        };
    };

    /** Manually invoke the error handing logic, letting it choose the correct error handling strategy. */
    invoke (
        invokedError: CustomError | any,
        customMessage?: string,
        customSupportId?: string
    ): void {
    // For manually invoking the switch logic.
        this.handleError(invokedError, customMessage, customSupportId);
    }

    /** Manually invoke an error strategy that only shows the message. */
    show (message?): void {
        new ShowNotificationStrategy(
            this.notifications.notify,
            this.logger.logError,
            this.getLoginUrlWithRedirect(),
            message
        ).execute();
    }

    /** Manually invoke an error strategy that shows the message and logs it. */
    showAndLog (error: CustomError, details = {}, message?: string): void {
        new GenericErrorStrategy(
            this.notifications.notify,
            this.logger.logError,
            this.getLoginUrlWithRedirect()
        ).invokeError(error, details, message); // We use the GenericErrorStrategy because we just want access to the build in invoke method.
    }
}
